import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import {
  WeekDayToggle,
  WEEKDAY_TOGGLE_DEFAULT_OPTIONS,
  type WeekDayToggleOption,
} from '../composites/WeekDayToggle';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

/** @deprecated Import from `@packages/ui/composites` — `WEEKDAY_TOGGLE_DEFAULT_OPTIONS`. */
export const WEEKDAY_TOGGLE_DEFAULT_DAYS = WEEKDAY_TOGGLE_DEFAULT_OPTIONS;

export type { WeekDayToggleOption };

export interface RhfWeekDayToggleProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  days?: readonly WeekDayToggleOption[];
}

function normalizeSelectedDays(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

export const RhfWeekDayToggle = React.forwardRef<HTMLDivElement, RhfWeekDayToggleProps>(
  function RhfWeekDayToggle(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      controlWidth,
      days = WEEKDAY_TOGGLE_DEFAULT_OPTIONS,
    },
    ref,
  ) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const selected = normalizeSelectedDays(field.value);

          return (
            <FormField
              label={label}
              hint={hint}
              tooltip={tooltip}
              semanticId={semanticId ?? name}
              required={required}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
              labelSpacing="md"
            >
              <WeekDayToggle
                ref={ref}
                semanticId={semanticId ?? name}
                value={selected}
                onChange={field.onChange}
                options={days}
                disabled={disabled}
                aria-label={label || 'Days of week'}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfWeekDayToggle.displayName = 'RhfWeekDayToggle';
